import { useState, useEffect } from 'react';
import axios from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';
import {
  Select, Button, Alert, Modal, Table, Typography, Space, Flex, Tag,
  App as AntApp,
} from 'antd';
import {
  DownloadOutlined, ReloadOutlined, BarChartOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { API_URL } from '../config';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import HistoricoDRE from './HistoricoDRE';
import { rotuloAreaReceita, mostrarAreaReceita } from '../utils/areasReceita';
import {
  isAnual, isSemestral, isMensal, trimestresDe, rotuloCobertura, pesoTrimestres,
} from '../utils/periodoDre';

const { Text } = Typography;

async function getToken(): Promise<string> {
  const session = await fetchAuthSession();
  return session.tokens?.idToken?.toString() ?? '';
}

interface DashboardProps {
  user: any;
  signOut: any;
}

const fmt = (v: number) =>
  'R$ ' + Math.abs(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const pct = (v: number, base: number) =>
  base ? ((v / base) * 100).toFixed(1) + '%' : '-';

function trimestreAnterior(): string {
  const now = new Date();
  let q = Math.floor(now.getMonth() / 3) + 1;
  let year = now.getFullYear();
  q -= 1;
  if (q === 0) { q = 4; year -= 1; }
  return `${year}/Q${q}`;
}

// Unidade → query string do backend (mês vai como periodo=, trimestre como trimestre=)
const qsUnidade = (u: string) =>
  u.includes('Q') ? `trimestre=${encodeURIComponent(u)}` : `periodo=${encodeURIComponent(u)}`;

// Mês → trimestre que o contém (pro indiretos-12m e afins)
const trimestreDoMes = (p: string) => {
  const [ano, m] = p.split('/');
  return `${ano}/Q${Math.floor((parseInt(m) - 1) / 3) + 1}`;
};

function deepSum(objs: any[]): any {
  const first = objs.find(o => o !== undefined && o !== null);
  if (first === undefined) return 0;
  if (typeof first === 'number') {
    return objs.reduce((s, v) => s + (typeof v === 'number' ? v : 0), 0);
  }
  if (typeof first === 'object' && !Array.isArray(first)) {
    const keySet: Record<string, true> = {};
    for (const o of objs) if (o && typeof o === 'object') Object.keys(o).forEach(k => { keySet[k] = true; });
    const out: any = {};
    Object.keys(keySet).forEach(k => { out[k] = deepSum(objs.map(o => o?.[k])); });
    return out;
  }
  return first;
}

const MESES_LABEL = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];

function labelPeriodo(p: string): string {
  // No ano em curso NÃO é o ano inteiro — a visão corta no mês atual.
  if (isAnual(p)) {
    return rotuloCobertura(p) ? `${p} — ano em curso` : `${p} — Ano inteiro`;
  }
  if (isSemestral(p)) {
    const [ano, s] = p.split('/');
    return s === 'S1' ? `1º semestre ${ano} (Jan–Jun)` : `2º semestre ${ano} (Jul–Dez)`;
  }
  if (isMensal(p)) {
    const [ano, m] = p.split('/');
    return `${MESES_LABEL[parseInt(m) - 1]} ${ano}`;
  }
  const [ano, q] = p.split('/');
  const meses: Record<string, string> = { Q1: 'Jan–Mar', Q2: 'Abr–Jun', Q3: 'Jul–Set', Q4: 'Out–Dez' };
  return `${q} ${ano} (${meses[q]})`;
}

const W_ORC = 130;
const W_REAL = 140;
const W_DIFF = 85;
const W_ARROW = 18;
const W_PCT = 70;

type TipoAnalise = 'anual' | 'semestral' | 'trimestral' | 'mensal';

function DashboardInner({ user: _user, signOut: _signOut }: DashboardProps) {
  const [trimestre, setTrimestre] = useState(trimestreAnterior());
  // Cascata do filtro (ano → tipo → período) — inicia no trimestre anterior
  const [anoSel, setAnoSel] = useState(() => parseInt(trimestreAnterior().split('/')[0]));
  const [tipoSel, setTipoSel] = useState<TipoAnalise>('trimestral');
  const [perSel, setPerSel] = useState<string>(() => trimestreAnterior().split('/')[1]);
  const [dre, setDre] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState('');
  const [expandidos, setExpandidos] = useState<Set<string>>(new Set());
  const [modalCategoria, setModalCategoria] = useState<string | null>(null);
  const [modalLancamentos, setModalLancamentos] = useState<any[]>([]);
  const [modalLoading, setModalLoading] = useState(false);
  // Orçado é opt-in (Lucas 2026-07-08): por padrão a DRE abre só com o
  // realizado; a preferência de quem liga fica no navegador.
  const [mostrarOrcamento, setMostrarOrcamento] = useState(() => {
    try { return localStorage.getItem('dre_vs_orcamento') === '1'; } catch { return false; }
  });
  const [orcamento, setOrcamento] = useState<Record<string, number>>({});
  const [taxSource, setTaxSource] = useState<'estimado' | 'realizado'>('estimado');
  const [indiretos12m, setIndiretos12m] = useState<{ pct: number; inicio: string; fim: string } | null>(null);

  // Orçamento é cadastrado por TRIMESTRE — na visão mensal não há régua justa
  const orcamentoOn = mostrarOrcamento && !isMensal(trimestre);

  // Auto-load (Lucas 2026-07-08): mudou o filtro, carrega — sem botão.
  useEffect(() => {
    carregarDRE();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trimestre, taxSource]);

  const toggleExpandido = (key: string) => {
    setExpandidos(prev => {
      const novo = new Set(prev);
      novo.has(key) ? novo.delete(key) : novo.add(key);
      return novo;
    });
  };

  async function carregarDRE() {
    setLoading(true); setErro('');
    try {
      const token = await getToken();
      const qs = `?tax_source=${taxSource}`;
      const trims = trimestresDe(trimestre);
      if (trims.length > 1) {
        const resps = await Promise.all(trims.map(t =>
          axios.get(`${API_URL}/dre/trimestre/${encodeURIComponent(t)}${qs}`, {
            headers: { Authorization: token },
          })
        ));
        const dres = resps.map(r => r.data.body ? JSON.parse(r.data.body) : r.data);
        setDre(deepSum(dres));
      } else {
        const t = encodeURIComponent(trims[0]);
        const res = await axios.get(`${API_URL}/dre/trimestre/${t}${qs}`, {
          headers: { Authorization: token },
        });
        const data = res.data.body ? JSON.parse(res.data.body) : res.data;
        setDre(data);
      }
      if (mostrarOrcamento) await carregarOrcamento();
      try {
        const ultimo = trims[trims.length - 1];
        const tri = ultimo.includes('Q') ? ultimo : trimestreDoMes(ultimo);
        const res12 = await axios.get(`${API_URL}/indiretos-12m?trimestre=${encodeURIComponent(tri)}`, {
          headers: { Authorization: token },
        });
        const data12 = res12.data.body ? JSON.parse(res12.data.body) : res12.data;
        setIndiretos12m({ pct: data12.pct, inicio: data12.inicio, fim: data12.fim });
      } catch { setIndiretos12m(null); }
    } catch (e: any) {
      setErro('Erro ao carregar DRE: ' + e.message);
    } finally { setLoading(false); }
  }

  async function carregarOrcamento() {
    if (isMensal(trimestre)) { setOrcamento({}); return; }
    try {
      const token = await getToken();
      // O orçamento é cadastrado POR TRIMESTRE (não tem quebra mensal), mas o
      // ano em curso agora corta no mês atual. Sem ajuste, o trimestre em
      // andamento compararia 2 meses realizados contra 3 meses orçados e toda
      // linha pareceria "abaixo do orçado".
      // Solução: os meses soltos viram o trimestre que os contém, com PESO
      // proporcional (2 meses de 3 = 2/3 do orçado daquele trimestre).
      const peso = pesoTrimestres(trimestresDe(trimestre));
      const tris = Object.keys(peso);
      const resps = await Promise.all(tris.map(t =>
        axios.get(`${API_URL}/orcamento?trimestre=${encodeURIComponent(t)}`, {
          headers: { Authorization: token },
        })
      ));
      const mapa: Record<string, number> = {};
      resps.forEach((res, i) => {
        const rows: any[] = res.data.body ? JSON.parse(res.data.body) : res.data;
        const p = peso[tris[i]] ?? 1;
        for (const r of rows) {
          mapa[r.linha_dre] = (mapa[r.linha_dre] || 0) + (parseFloat(r.valor_orcado) || 0) * p;
        }
      });
      setOrcamento(mapa);
    } catch {}
  }

  async function toggleOrcamento() {
    const novo = !mostrarOrcamento;
    setMostrarOrcamento(novo);
    try { localStorage.setItem('dre_vs_orcamento', novo ? '1' : '0'); } catch {}
    if (novo) await carregarOrcamento();
  }

  async function abrirModal(categoria: string) {
    setModalCategoria(categoria);
    setModalLoading(true);
    setModalLancamentos([]);
    try {
      const token = await getToken();
      const c = encodeURIComponent(categoria);
      const trims = trimestresDe(trimestre);
      const resps = await Promise.all(trims.map(t =>
        axios.get(`${API_URL}/lancamentos/categoria?categoria=${c}&${qsUnidade(t)}`, {
          headers: { Authorization: token },
        })
      ));
      const all: any[] = [];
      for (const res of resps) {
        const data = res.data.body ? JSON.parse(res.data.body) : res.data;
        if (Array.isArray(data)) all.push(...data);
      }
      setModalLancamentos(all);
    } finally { setModalLoading(false); }
  }

  const rb = dre?.receitas?.recBruta || 0;

  const rowStyle = (bold: boolean, level: number = 0): React.CSSProperties => ({
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: `5px ${level === 0 ? 12 : 24}px`,
    background: bold ? 'var(--bg-header, #f5f5f5)' : 'transparent',
    borderBottom: bold ? '1px solid #e0e0e0' : '0.5px solid #f0f0f0',
    fontWeight: bold ? 500 : 400, fontSize: 13,
  });

  const headerStyle: React.CSSProperties = {
    padding: '8px 12px', color: '#fff', fontSize: 12, fontWeight: 500,
    letterSpacing: '0.04em', cursor: 'pointer',
    display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 2,
  };

  const valStyle = (v: number): React.CSSProperties => ({
    color: v >= 0 ? '#0930D0' : '#E24B4A', minWidth: 140, textAlign: 'right',
  });

  const pctStyle: React.CSSProperties = {
    color: '#999', minWidth: 60, textAlign: 'right', fontSize: 12,
  };

  function Linha({ label, valor, bold = false, level = 0, categoria }: {
    label: string, valor: number, bold?: boolean, level?: number, categoria?: string
  }) {
    const orcado = (orcamentoOn && orcamento[label] !== undefined) ? orcamento[label] : undefined;
    const delta = orcado !== undefined ? valor - orcado : undefined;
    const deltaPct = (delta !== undefined && orcado !== undefined && orcado !== 0) ? (delta / Math.abs(orcado)) * 100 : undefined;
    return (
      <div
        style={{ ...rowStyle(bold, level), cursor: categoria ? 'pointer' : 'default' }}
        onClick={() => categoria && abrirModal(categoria)}
        title={categoria ? `Ver lançamentos de ${label}` : undefined}
      >
        <span style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 6 }}>
          {label}
          {categoria && <span style={{ fontSize: 10, color: '#aaa' }}>▶</span>}
        </span>
        {orcamentoOn ? (
          <>
            <span style={{ width: W_ORC, textAlign: 'right', fontSize: 12, color: '#888' }}>
              {orcado !== undefined ? fmt(orcado) : ''}
            </span>
            <span style={{ ...valStyle(valor), width: W_REAL, minWidth: W_REAL }}>{fmt(valor)}</span>
            <span style={{
              width: W_DIFF, textAlign: 'right', fontSize: 12, fontWeight: 500,
              color: deltaPct !== undefined ? (delta! >= 0 ? '#0930D0' : '#E24B4A') : '#ccc',
            }}>
              {deltaPct !== undefined ? `${delta! >= 0 ? '+' : ''}${deltaPct.toFixed(1)}%` : ''}
            </span>
            <span style={{ width: W_ARROW }} />
          </>
        ) : (
          <>
            <span style={{ ...valStyle(valor), width: W_REAL, minWidth: W_REAL }}>{fmt(valor)}</span>
            <span style={{ ...pctStyle, width: W_PCT, minWidth: W_PCT }}>{pct(valor, rb)}</span>
            <span style={{ width: W_ARROW }} />
          </>
        )}
      </div>
    );
  }

  function Grupo({ id, label, total, children, tipo = 'neutro', orcKey }: {
    id: string, label: string, total?: number, children: React.ReactNode,
    tipo?: 'receita' | 'despesa' | 'neutro', orcKey?: string
  }) {
    const aberto = expandidos.has(id);
    const bgVerde = tipo === 'receita' && id === 'rec';
    const orcado = (orcamentoOn && orcKey && orcamento[orcKey] !== undefined) ? orcamento[orcKey] : undefined;
    const delta = (orcado !== undefined && total !== undefined) ? total - orcado : undefined;
    const deltaPct = (delta !== undefined && orcado !== undefined && orcado !== 0) ? (delta / Math.abs(orcado)) * 100 : undefined;
    const deltaColor = bgVerde
      ? (delta !== undefined && delta >= 0 ? '#7BE0FF' : '#FFD4D3')
      : (delta !== undefined && delta >= 0 ? '#0930D0' : '#E24B4A');
    const orcColor = bgVerde ? 'rgba(255,255,255,0.85)' : '#555';
    return (
      <div style={{ marginBottom: 2 }}>
        <div style={{
          ...headerStyle,
          background: bgVerde ? '#0930D0' : '#f0f0f0',
          color: bgVerde ? '#fff' : tipo === 'despesa' ? '#B91626' : '#0930D0',
        }} onClick={() => toggleExpandido(id)}>
          <span style={{ flex: 1, minWidth: 0 }}>{label}</span>
          {orcamentoOn ? (
            <>
              <span style={{ width: W_ORC, textAlign: 'right', fontSize: 12, fontWeight: 400, color: orcColor }}>
                {orcado !== undefined ? fmt(orcado) : ''}
              </span>
              <span style={{ width: W_REAL, textAlign: 'right', fontSize: 13 }}>
                {total !== undefined ? fmt(total) : ''}
              </span>
              <span style={{
                width: W_DIFF, textAlign: 'right', fontSize: 12, fontWeight: 600,
                color: deltaPct !== undefined ? deltaColor : (bgVerde ? 'rgba(255,255,255,0.4)' : '#ccc'),
              }}>
                {deltaPct !== undefined ? `${delta! >= 0 ? '+' : ''}${deltaPct.toFixed(1)}%` : ''}
              </span>
            </>
          ) : (
            <>
              <span style={{ width: W_REAL, textAlign: 'right', fontSize: 13 }}>
                {total !== undefined ? fmt(total) : ''}
              </span>
              <span style={{ width: W_PCT, textAlign: 'right', fontSize: 11, opacity: 0.8 }}>
                {total !== undefined ? pct(total, rb) : ''}
              </span>
            </>
          )}
          <span style={{ width: W_ARROW, fontSize: 10, textAlign: 'right' }}>{aberto ? '▲' : '▼'}</span>
        </div>
        {aberto && <div style={{ borderLeft: '2px solid #0930D0' }}>{children}</div>}
      </div>
    );
  }

  function Resultado({ label, valor, orcKey }: { label: string, valor: number, orcKey?: string }) {
    const orcado = (orcamentoOn && orcKey && orcamento[orcKey] !== undefined)
      ? orcamento[orcKey] : undefined;
    const delta = orcado !== undefined ? valor - orcado : undefined;
    const deltaPct = (orcado !== undefined && orcado !== 0) ? (delta! / Math.abs(orcado)) * 100 : undefined;
    return (
      <div style={{
        display: 'flex', alignItems: 'center',
        padding: '10px 12px', background: valor >= 0 ? '#EDF0FF' : '#FEF0EF',
        borderLeft: `3px solid ${valor >= 0 ? '#0930D0' : '#E24B4A'}`,
        margin: '4px 0', fontSize: 14, fontWeight: 500,
      }}>
        <span style={{ flex: 1, minWidth: 0 }}>{label}</span>
        {orcamentoOn ? (
          <>
            <span style={{ width: W_ORC, textAlign: 'right', fontSize: 12, fontWeight: 400, color: '#666' }}>
              {orcado !== undefined ? fmt(orcado) : ''}
            </span>
            <span style={{ ...valStyle(valor), width: W_REAL, minWidth: W_REAL }}>{fmt(valor)}</span>
            <span style={{
              width: W_DIFF, textAlign: 'right', fontSize: 12, fontWeight: 600,
              color: deltaPct !== undefined ? (delta! >= 0 ? '#0930D0' : '#E24B4A') : '#ccc',
            }}>
              {deltaPct !== undefined ? `${delta! >= 0 ? '+' : ''}${deltaPct.toFixed(1)}%` : ''}
            </span>
            <span style={{ width: W_ARROW }} />
          </>
        ) : (
          <>
            <span style={{ ...valStyle(valor), width: W_REAL, minWidth: W_REAL }}>{fmt(valor)}</span>
            <span style={{ ...pctStyle, width: W_PCT, minWidth: W_PCT }}>{pct(valor, rb)}</span>
            <span style={{ width: W_ARROW }} />
          </>
        )}
      </div>
    );
  }

  const det = (obj: any) => Object.entries(obj || {})
    .sort((a, b) => (a[1] as number) - (b[1] as number));

  function exportarExcel() {
    const linhas: any[][] = [
      ['DEMONSTRATIVO DE RESULTADO DO EXERCÍCIO', '', ''],
      [labelPeriodo(trimestre), '', ''],
      ['', '', ''],
      ['LINHA', 'VALOR (R$)', '% RECEITA'],
      ['RECEITA BRUTA TOTAL', dre.receitas.recBruta, pct(dre.receitas.recBruta, rb)],
      ['  Topografia', dre.receitas.areas.topografia, pct(dre.receitas.areas.topografia, rb)],
      ['  Consultoria Ambiental', dre.receitas.areas.consultoria, pct(dre.receitas.areas.consultoria, rb)],
      ['  Projeto', dre.receitas.areas.projeto, pct(dre.receitas.areas.projeto, rb)],
      ['  Hidrometria', dre.receitas.areas.hidrometria, pct(dre.receitas.areas.hidrometria, rb)],
      ['  Fiscalização de Projeto', dre.receitas.areas.fiscalizacao, pct(dre.receitas.areas.fiscalizacao, rb)],
      ['  Segurança de Barragens', dre.receitas.areas.segbarr, pct(dre.receitas.areas.segbarr, rb)],
      ...(Math.abs(dre.receitas.areas.sem_setor || 0) >= 0.01
        ? [['  Sem setor (classificar)', dre.receitas.areas.sem_setor, pct(dre.receitas.areas.sem_setor, rb)]]
        : []),
      ['', '', ''],
      ['COMISSÕES DE VENDAS', dre.receitas.comissoes, pct(dre.receitas.comissoes, rb)],
      ['', '', ''],
      ['RECEITA LÍQUIDA COMERCIAL', dre.receitas.recLiqComercial, pct(dre.receitas.recLiqComercial, rb)],
      ['', '', ''],
      ['CUSTOS OPERACIONAIS', dre.custos.custosOp, pct(dre.custos.custosOp, rb)],
      ...Object.entries(dre.custos.detalhe || {}).map(([k, v]) => [`  ${k}`, v, pct(v as number, rb)]),
      ['', '', ''],
      ['LUCRO BRUTO (MARGEM DE CONTRIBUIÇÃO)', dre.lucroBruto, pct(dre.lucroBruto, rb)],
      ['', '', ''],
      ['DESPESAS COMERCIAIS E DE MARKETING', dre.despesas.despComercial, pct(dre.despesas.despComercial, rb)],
      ...Object.entries(dre.despesas.detalhe.comercial || {}).map(([k, v]) => [`  ${k}`, v, pct(v as number, rb)]),
      ['', '', ''],
      ['DESPESAS ADMINISTRATIVAS', dre.despesas.despAdmin, pct(dre.despesas.despAdmin, rb)],
      ...Object.entries(dre.despesas.detalhe.admin || {}).map(([k, v]) => [`  ${k}`, v, pct(v as number, rb)]),
      ['', '', ''],
      ['OUTRAS DESPESAS OPERACIONAIS', dre.despesas.outrasDespOp, pct(dre.despesas.outrasDespOp, rb)],
      ...Object.entries(dre.despesas.detalhe.outras || {}).map(([k, v]) => [`  ${k}`, v, pct(v as number, rb)]),
      ['', '', ''],
      ['RESULTADO OPERACIONAL', dre.resultadoOp, pct(dre.resultadoOp, rb)],
      ['', '', ''],
      ['DESPESAS FINANCEIRAS', dre.financeiro.despFin, pct(dre.financeiro.despFin, rb)],
      ...Object.entries(dre.financeiro.detalhe.despesas || {}).map(([k, v]) => [`  ${k}`, v, pct(v as number, rb)]),
      ['', '', ''],
      ['RECEITAS FINANCEIRAS', dre.financeiro.recFin, pct(dre.financeiro.recFin, rb)],
      ...Object.entries(dre.financeiro.detalhe.receitas || {}).map(([k, v]) => [`  ${k}`, v, pct(v as number, rb)]),
      ['', '', ''],
      ['RESULTADO ANTES DOS TRIBUTOS', dre.resultadoAntes, pct(dre.resultadoAntes, rb)],
      ['', '', ''],
      ['DEDUÇÕES (ISS + Retenções)', -(dre.receitas.issTotalNF + dre.receitas.retencoes), pct(-(dre.receitas.issTotalNF + dre.receitas.retencoes), rb)],
      ['  ISS', -dre.receitas.issTotalNF, pct(-dre.receitas.issTotalNF, rb)],
      ['  IR + PIS + CSLL + COFINS retidos', -dre.receitas.retencoes, pct(-dre.receitas.retencoes, rb)],
      ['', '', ''],
      ['IMPOSTOS', dre.impostos.total, pct(dre.impostos.total, rb)],
      ...Object.entries(dre.impostos.detalhe || {}).map(([k, v]) => [`  ${k}`, v, pct(v as number, rb)]),
      ['', '', ''],
      ['LUCRO LÍQUIDO', dre.lucroLiq, pct(dre.lucroLiq, rb)],
      ['', '', ''],
      ['INVESTIMENTOS EM IMOBILIZADO', dre.investimentos.total, pct(dre.investimentos.total, rb)],
      ...Object.entries(dre.investimentos.detalhe || {}).map(([k, v]) => [`  ${k}`, v, pct(v as number, rb)]),
      ['', '', ''],
      ['LUCRO FINAL (RESULTADO DO EXERCÍCIO)', dre.lucroFinal, pct(dre.lucroFinal, rb)],
    ];
    const ws = XLSX.utils.aoa_to_sheet(linhas);
    ws['!cols'] = [{ wch: 50 }, { wch: 18 }, { wch: 12 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'DRE');
    const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const fname = isAnual(trimestre)
      ? `DRE_${trimestre}_Anual.xlsx`
      : `DRE_${trimestre.replace('/', '_')}.xlsx`;
    saveAs(new Blob([buf], { type: 'application/octet-stream' }), fname);
  }

  const anoAtual = new Date().getFullYear();
  const anos = [anoAtual, anoAtual - 1, anoAtual - 2];
  const qMeses: Record<string, string> = { Q1: 'Jan–Mar', Q2: 'Abr–Jun', Q3: 'Jul–Set', Q4: 'Out–Dez' };

  // Cascata: ano → tipo de análise → período específico → compõe `trimestre`
  // ("2026", "2026/S1", "2026/Q2" ou "2026/05") e o auto-load faz o resto.
  function aplicarCascata(ano: number, tipo: TipoAnalise, per: string | null) {
    setAnoSel(ano);
    setTipoSel(tipo);
    let p = per;
    if (p === null) {
      // trocou ano/tipo: reaproveita o período se compatível, senão o primeiro
      const compat =
        (tipo === 'semestral' && /^S[12]$/.test(perSel)) ||
        (tipo === 'trimestral' && /^Q[1-4]$/.test(perSel)) ||
        (tipo === 'mensal' && /^\d{2}$/.test(perSel));
      p = compat ? perSel : tipo === 'semestral' ? 'S1' : tipo === 'trimestral' ? 'Q1' : '01';
    }
    setPerSel(p);
    setTrimestre(tipo === 'anual' ? String(ano) : `${ano}/${p}`);
  }

  const colsModal: ColumnsType<any> = [
    { title: 'Data', dataIndex: 'data_competencia', key: 'd', width: 100, render: (v) => v?.split('T')[0] },
    { title: 'Descrição', dataIndex: 'descricao', key: 'desc' },
    { title: 'Fornecedor', dataIndex: 'fornecedor_original', key: 'forn' },
    {
      title: 'Valor', dataIndex: 'valor', key: 'v', align: 'right', width: 130,
      // Receita (rendimentos etc.) em verde; despesa em vermelho
      render: (v) => {
        const n = parseFloat(v);
        return <Text style={{ color: n > 0 ? '#0a7f4a' : '#E24B4A', fontWeight: 500 }}>{fmt(n)}</Text>;
      },
    },
  ];

  return (
    <div style={{ maxWidth: 1440, margin: '16px auto', padding: '0 24px' }}>
      <Flex gap={12} align="center" wrap="wrap" style={{ marginBottom: 16 }}>
        {/* Filtro em cascata (Lucas 2026-07-08): ano → tipo de análise →
            período. Carrega sozinho ao mudar (auto-load no useEffect). */}
        <Select
          value={anoSel}
          onChange={(a) => aplicarCascata(a, tipoSel, null)}
          options={anos.map(a => ({ label: String(a), value: a }))}
          style={{ width: 96 }}
        />
        <Select
          value={tipoSel}
          onChange={(t) => aplicarCascata(anoSel, t, null)}
          options={[
            { label: 'Anual', value: 'anual' },
            { label: 'Semestral', value: 'semestral' },
            { label: 'Trimestral', value: 'trimestral' },
            { label: 'Mensal', value: 'mensal' },
          ]}
          style={{ width: 130 }}
        />
        {tipoSel !== 'anual' && (
          <Select
            value={perSel}
            onChange={(p) => aplicarCascata(anoSel, tipoSel, p)}
            options={
              tipoSel === 'semestral'
                ? [{ label: '1º semestre (Jan–Jun)', value: 'S1' },
                   { label: '2º semestre (Jul–Dez)', value: 'S2' }]
                : tipoSel === 'trimestral'
                  ? ['Q1', 'Q2', 'Q3', 'Q4'].map(q => ({ label: `${q} (${qMeses[q]})`, value: q }))
                  : MESES_LABEL.map((m, i) => ({
                      label: m, value: String(i + 1).padStart(2, '0'),
                    }))
            }
            style={{ minWidth: 180 }}
          />
        )}
        {loading && <ReloadOutlined spin style={{ color: '#0930D0' }} />}
        <Button
          type={mostrarOrcamento ? 'primary' : 'default'}
          ghost={mostrarOrcamento}
          icon={<BarChartOutlined />}
          onClick={toggleOrcamento}
          disabled={isMensal(trimestre)}
          title={isMensal(trimestre) ? 'Orçamento é cadastrado por trimestre — indisponível na visão mensal' : undefined}
        >
          {mostrarOrcamento ? 'Ocultar Orçamento' : 'vs Orçamento'}
        </Button>
        {dre && (
          <Button icon={<DownloadOutlined />} onClick={exportarExcel}>
            Exportar XLSX
          </Button>
        )}
      </Flex>

      {erro && (
        <Alert type="error" message={erro} showIcon closable onClose={() => setErro('')} style={{ marginBottom: 12 }} />
      )}

      {/* Ano em curso: deixa explícito o período realmente coberto. Sem isso,
          "Anual" parece o ano fechado — e antes de 14/08/2026 a tela chegava a
          mostrar PREJUÍZO por somar 12 meses de despesa contra 8 de receita. */}
      {rotuloCobertura(trimestre) && (
        <Alert type="info" showIcon style={{ marginBottom: 12 }}
          message={<>Mostrando <b>{rotuloCobertura(trimestre)}</b></>}
          description={
            <span style={{ fontSize: 12 }}>
              Meses que ainda não chegaram ficam de fora: eles já têm despesa lançada
              (parcelas, provisões, DARF) mas a receita só é reconhecida quando fatura —
              incluí-los mostraria um prejuízo que não existe.
            </span>
          } />
      )}

      <div className="dre-layout" style={{ display: 'flex', gap: 24, alignItems: 'flex-start' }}>
        <div className="dre-col-esq" style={{ flex: '0 1 680px', minWidth: 0 }}>
          {dre && (
            <div style={{ border: '0.5px solid #e0e0e0', borderRadius: 8, overflow: 'hidden' }}>
              {mostrarOrcamento && (
                <div style={{
                  display: 'flex', alignItems: 'center', padding: '8px 12px',
                  background: '#020D7F', color: '#fff', fontSize: 10, fontWeight: 700,
                  letterSpacing: '0.1em',
                }}>
                  <span style={{ flex: 1, minWidth: 0 }} />
                  <span style={{ width: W_ORC, textAlign: 'right' }}>ORÇADO</span>
                  <span style={{ width: W_REAL, textAlign: 'right' }}>REALIZADO</span>
                  <span style={{ width: W_DIFF, textAlign: 'right' }}>DIFERENÇA</span>
                  <span style={{ width: W_ARROW }} />
                </div>
              )}

              <Grupo id="rec" label="RECEITA BRUTA TOTAL" total={dre.receitas.recBruta} tipo="receita" orcKey="recBruta">
                {Object.entries(dre.receitas.areas || {}).filter(([k, v]) => mostrarAreaReceita(k, v as number, dre.receitas.semSetorParcelas)).map(([k, v]) => (
                  <Linha key={k} label={rotuloAreaReceita(k)} valor={v as number}
                    level={1} categoria={`receita:${k}`} />
                ))}
              </Grupo>
              {(Math.abs(dre.receitas.areas?.sem_setor || 0) >= 0.01 || (dre.receitas.semSetorParcelas || 0) > 0) && (
                <Alert
                  type="warning" showIcon style={{ margin: '4px 0 12px' }}
                  message={(dre.receitas.areas.sem_setor as number) < 0
                    ? 'Há parcela com a divisão por setor passando de 100%'
                    : `${((dre.receitas.areas.sem_setor as number) || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })} da receita está sem setor`
                      + ` (${dre.receitas.semSetorParcelas} ${dre.receitas.semSetorParcelas === 1 ? 'item' : 'itens'})`}
                  description={<>A receita total está certa; só a divisão por setor não. Clique em <strong>{rotuloAreaReceita('sem_setor')}</strong> para ver quais são
                    e acerte o setor em <strong>Financeiro → Contas a Receber</strong>
                    {(dre.receitas.semSetorManuais || 0) > 0 && <> (os lançamentos manuais, em <strong>Lançamentos Manuais</strong>)</>}.</>}
                />
              )}

              <Grupo id="com" label="COMISSÕES DE VENDAS" total={dre.receitas.comissoes} tipo="despesa">
                {det(dre.receitas.comissoesDetalhe).map(([k, v]) => (
                  <Linha key={k} label={k} valor={v as number} level={1} categoria={k} />
                ))}
              </Grupo>

              <Resultado label="RECEITA LÍQUIDA COMERCIAL" valor={dre.receitas.recLiqComercial} orcKey="recLiqComercial" />

              <Grupo id="custos" label="CUSTOS OPERACIONAIS" total={dre.custos.custosOp} tipo="despesa" orcKey="custosOp">
                {det(dre.custos.detalhe).map(([k, v]) => (
                  <Linha key={k} label={k} valor={v as number} level={1} categoria={k} />
                ))}
              </Grupo>

              <Resultado label="LUCRO BRUTO (MARGEM DE CONTRIBUIÇÃO)" valor={dre.lucroBruto} orcKey="lucroBruto" />

              <Grupo id="dc" label="DESPESAS COMERCIAIS E DE MARKETING" total={dre.despesas.despComercial} tipo="despesa" orcKey="despComercial">
                {det(dre.despesas.detalhe.comercial).map(([k, v]) => (
                  <Linha key={k} label={k} valor={v as number} level={1} categoria={k} />
                ))}
              </Grupo>

              <Grupo id="da" label="DESPESAS ADMINISTRATIVAS" total={dre.despesas.despAdmin} tipo="despesa" orcKey="despAdmin">
                {det(dre.despesas.detalhe.admin).map(([k, v]) => (
                  <Linha key={k} label={k} valor={v as number} level={1} categoria={k} />
                ))}
              </Grupo>

              <Grupo id="od" label="OUTRAS DESPESAS OPERACIONAIS" total={dre.despesas.outrasDespOp} tipo="despesa" orcKey="outrasDespOp">
                {det(dre.despesas.detalhe.outras).map(([k, v]) => (
                  <Linha key={k} label={k} valor={v as number} level={1} categoria={k} />
                ))}
              </Grupo>

              {dre.outrasReceitasOp && (
                <Grupo id="oro" label="OUTRAS RECEITAS OPERACIONAIS" total={dre.outrasReceitasOp.total} tipo="receita">
                  {det(dre.outrasReceitasOp.detalhe).map(([k, v]) => (
                    <Linha key={k} label={k} valor={v as number} level={1} categoria={k} />
                  ))}
                </Grupo>
              )}

              {dre.estorno && (
                <Grupo id="est" label="ESTORNO" total={dre.estorno.total} tipo="receita">
                  {det(dre.estorno.detalhe).map(([k, v]) => (
                    <Linha key={k} label={k} valor={v as number} level={1} categoria={k} />
                  ))}
                </Grupo>
              )}

              <Resultado label="RESULTADO OPERACIONAL" valor={dre.resultadoOp} orcKey="resultadoOp" />

              <Grupo id="df" label="DESPESAS FINANCEIRAS" total={dre.financeiro.despFin} tipo="despesa" orcKey="despFin">
                {det(dre.financeiro.detalhe.despesas).map(([k, v]) => (
                  <Linha key={k} label={k} valor={v as number} level={1} categoria={k} />
                ))}
              </Grupo>

              <Grupo id="rf" label="RECEITAS FINANCEIRAS" total={dre.financeiro.recFin} tipo="receita" orcKey="recFin">
                {det(dre.financeiro.detalhe.receitas).map(([k, v]) => (
                  <Linha key={k} label={k} valor={v as number} level={1} categoria={k} />
                ))}
              </Grupo>

              <Resultado label="RESULTADO ANTES DOS TRIBUTOS" valor={dre.resultadoAntes} />

              {dre.deducoes && (
                <Grupo id="ded" label="DEDUÇÕES" total={dre.deducoes.total} tipo="despesa">
                  {Object.entries(dre.deducoes.detalhe || {}).map(([k, v]) => (
                    <Linha key={k} label={k} valor={v as number} level={1} />
                  ))}
                </Grupo>
              )}

              <Grupo id="imp" label="IMPOSTOS" total={dre.impostos.total} tipo="despesa" orcKey="impostos">
                {det(dre.impostos.detalhe).map(([k, v]) => (
                  <Linha key={k} label={k} valor={v as number} level={1} categoria={k} />
                ))}
                {dre.impostos.estimado && dre.impostos.realizado && (() => {
                  const est = dre.impostos.estimado.total as number;
                  const real = dre.impostos.realizado.total as number;
                  const temDarfs = dre.impostos.realizado.tem_lancamentos;
                  const delta = real - est;
                  return (
                    <div style={{
                      marginTop: 8, padding: '10px 12px', borderTop: '0.5px dashed #ddd',
                      fontSize: 11, color: '#666',
                    }}>
                      <Flex align="center" gap={10} wrap="wrap">
                        <Tag color={taxSource === 'estimado' ? 'blue' : 'default'}>
                          Modo: {taxSource === 'estimado' ? 'Estimativa fiscal' : 'DARFs realizadas'}
                        </Tag>
                        <Text style={{ fontSize: 11 }}>Estimativa: {fmt(est)}</Text>
                        <Text style={{ fontSize: 11 }}>
                          DARFs: {fmt(real)} {!temDarfs && <Text type="secondary" italic>(nenhum lançamento)</Text>}
                        </Text>
                        {temDarfs && (
                          <Text style={{ color: Math.abs(delta) < 0.01 ? '#0a7f4a' : '#B07500', fontWeight: 500, fontSize: 11 }}>
                            Δ: {delta >= 0 ? '+' : '−'}{fmt(delta)}
                          </Text>
                        )}
                        <Button
                          size="small" type="default" style={{ marginLeft: 'auto' }}
                          disabled={!temDarfs && taxSource === 'estimado'}
                          onClick={() => setTaxSource(taxSource === 'estimado' ? 'realizado' : 'estimado')}
                          title={!temDarfs && taxSource === 'estimado' ? 'Nenhuma DARF lançada neste período' : ''}
                        >
                          {taxSource === 'estimado' ? 'Atualizar com DARFs →' : '← Voltar para estimativa'}
                        </Button>
                      </Flex>
                    </div>
                  );
                })()}
              </Grupo>

              <Resultado label="LUCRO LÍQUIDO" valor={dre.lucroLiq} orcKey="lucroLiq" />

              <Grupo id="inv" label="INVESTIMENTOS EM IMOBILIZADO" total={dre.investimentos.total} tipo="despesa" orcKey="investimentos">
                {det(dre.investimentos.detalhe).map(([k, v]) => (
                  <Linha key={k} label={k} valor={v as number} level={1} categoria={k} />
                ))}
              </Grupo>

              <Resultado label="LUCRO FINAL (RESULTADO DO EXERCÍCIO)" valor={dre.lucroFinal} orcKey="lucroFinal" />

              {dre.naoMapeadosForaDaDre && Object.keys(dre.naoMapeadosForaDaDre).length > 0 && (
                <>
                  <Alert
                    type="warning" showIcon style={{ marginTop: 20 }}
                    message="Despesas sem categoria — fora da DRE"
                    description={<>Estes lançamentos não entram em nenhum total acima. Informe a categoria deles no <strong>Contas a Pagar</strong>.</>}
                  />
                  <Grupo id="naomap" label="DESPESAS SEM CATEGORIA"
                    total={Object.values(dre.naoMapeadosForaDaDre as Record<string, number>).reduce((a, b) => a + b, 0)} tipo="despesa">
                    {det(dre.naoMapeadosForaDaDre).map(([k, v]) => (
                      <Linha key={k} label={k} valor={v as number} level={1} categoria={k} />
                    ))}
                  </Grupo>
                </>
              )}

              {dre.pendentes && dre.pendentes.total !== 0 && (
                <>
                  <Alert
                    type="warning" showIcon style={{ marginTop: 20 }}
                    message="Categorias pendentes de classificação"
                    description={<>Esses lançamentos ainda não estão integrados à DRE acima. Vá em <strong>Categorias</strong> para classificar.</>}
                  />
                  <Grupo id="pend" label="CATEGORIAS PENDENTES" total={dre.pendentes.total} tipo="despesa">
                    {det(dre.pendentes.detalhe).map(([k, v]) => (
                      <Linha key={k} label={k} valor={v as number} level={1} categoria={k} />
                    ))}
                  </Grupo>
                </>
              )}
            </div>
          )}
        </div>

        <div className="dre-col-dir" style={{ flex: '1 1 480px', minWidth: 420, display: 'flex', flexDirection: 'column', gap: 16, position: 'sticky', top: 24 }}>
          {dre && (() => {
            const receita = dre.receitas?.recBruta ?? 0;
            const ll = dre.lucroLiq ?? 0;
            const despesas = receita - ll;
            const positivo = ll >= 0;

            const cardBase: React.CSSProperties = {
              flex: '1 1 0', borderRadius: 10, padding: '16px 14px', color: '#fff', minWidth: 0,
            };

            const miniCard = (
              titulo: string, valor: number, bg: string, orcKey?: string, subline?: string,
            ) => {
              const orc = orcKey ? orcamento[orcKey] : undefined;
              const d = orc !== undefined ? valor - orc : undefined;
              const dp = (d !== undefined && orc && orc !== 0) ? (d / Math.abs(orc)) * 100 : undefined;
              return (
                <div style={{ ...cardBase, background: bg }}>
                  <div style={{ fontSize: 9, fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase', opacity: 0.75, marginBottom: 6 }}>
                    {titulo}
                  </div>
                  <div style={{ fontSize: 22, fontWeight: 700, letterSpacing: '-0.02em' }}>
                    {valor < 0 ? '- ' : ''}{fmt(valor)}
                  </div>
                  {mostrarOrcamento && orc !== undefined && (
                    <div style={{ marginTop: 6, display: 'flex', gap: 8, fontSize: 11, opacity: 0.85, flexWrap: 'wrap' }}>
                      <span>Orç: {fmt(orc)}</span>
                      {dp !== undefined && (
                        <span style={{ background: 'rgba(255,255,255,0.18)', borderRadius: 4, padding: '0 6px', fontWeight: 600 }}>
                          {d! >= 0 ? '+' : ''}{dp.toFixed(1)}%
                        </span>
                      )}
                    </div>
                  )}
                  {subline && <div style={{ marginTop: 4, fontSize: 10, opacity: 0.55 }}>{subline}</div>}
                </div>
              );
            };

            const indiretos = (dre.despesas?.despComercial || 0)
              + (dre.despesas?.despAdmin || 0)
              + (dre.despesas?.outrasDespOp || 0)
              + (dre.financeiro?.despFin || 0);
            const pctIndiretos = receita > 0 ? (Math.abs(indiretos) / receita) * 100 : 0;

            return (
              <>
                <Flex gap={10}>
                  {miniCard('Receita', receita, 'linear-gradient(135deg, #02B5DB 0%, #0930D0 100%)', 'recBruta')}
                  {miniCard('Despesas', despesas, 'linear-gradient(135deg, #E24B4A 0%, #B91626 100%)', undefined, 'Sem investimentos')}
                  {miniCard('Lucro Líquido', ll,
                    positivo
                      ? 'linear-gradient(135deg, #0930D0 0%, #020D7F 100%)'
                      : 'linear-gradient(135deg, #E24B4A 0%, #B91626 100%)',
                    'lucroLiq',
                    rb > 0 ? `Margem: ${((ll / rb) * 100).toFixed(1)}%` : undefined)}
                </Flex>
                <div style={{
                  marginTop: -6, padding: '10px 14px', borderRadius: 10,
                  background: 'linear-gradient(135deg, #F7F7FD 0%, #E7ECFD 100%)',
                  border: '0.5px solid #D0D8F5',
                }}>
                  <div style={{ fontSize: 9, fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#6A7092', marginBottom: 6 }}>
                    % Indiretos sobre Receita
                  </div>
                  <div style={{ fontSize: 10, color: '#888', marginBottom: 8 }}>
                    Desp. Comerciais + Admin + Outras Op + Financeiras · para markup
                  </div>
                  <Flex gap={16} align="flex-end">
                    <div style={{ flex: 1, borderRight: '0.5px solid #D0D8F5', paddingRight: 16 }}>
                      <div style={{ fontSize: 9, color: '#6A7092', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 2 }}>
                        Período atual
                      </div>
                      <div style={{ fontSize: 22, fontWeight: 700, color: '#0930D0', letterSpacing: '-0.02em', lineHeight: 1 }}>
                        {pctIndiretos.toFixed(1)}%
                      </div>
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 9, color: '#6A7092', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 2 }}>
                        Média 12 meses
                        {indiretos12m && (
                          <span style={{ textTransform: 'none', letterSpacing: 0, color: '#aaa', marginLeft: 4, fontSize: 9 }}>
                            ({indiretos12m.inicio.replace('/', '·')} → {indiretos12m.fim.replace('/', '·')})
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: 22, fontWeight: 700, color: '#02B5DB', letterSpacing: '-0.02em', lineHeight: 1 }}>
                        {indiretos12m ? `${indiretos12m.pct.toFixed(1)}%` : '—'}
                      </div>
                    </div>
                  </Flex>
                </div>
              </>
            );
          })()}

          <HistoricoDRE />
        </div>
      </div>

      <Modal
        open={!!modalCategoria}
        onCancel={() => setModalCategoria(null)}
        footer={null}
        width={720}
        title={
          <Space direction="vertical" size={2}>
            <Text strong style={{ fontSize: 14 }}>
              {modalCategoria?.startsWith('receita:')
                ? 'Receita — ' + rotuloAreaReceita(modalCategoria.slice(8))
                : modalCategoria}
            </Text>
            <Text type="secondary" style={{ fontSize: 11 }}>{labelPeriodo(trimestre)}</Text>
          </Space>
        }
      >
        <Table
          rowKey="id"
          columns={colsModal}
          dataSource={modalLancamentos}
          loading={modalLoading}
          size="small"
          pagination={{ pageSize: 50 }}
          summary={(data) => {
            const total = data.reduce((s: number, l: any) => s + (parseFloat(l.valor) || 0), 0);
            return (
              <Table.Summary fixed>
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0} colSpan={3}>
                    <Text strong>Total ({data.length} lançamentos)</Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={3} align="right">
                    <Text strong style={{ color: total > 0 ? '#0a7f4a' : '#E24B4A' }}>{fmt(total)}</Text>
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              </Table.Summary>
            );
          }}
        />
      </Modal>
    </div>
  );
}

export default function Dashboard(props: DashboardProps) {
  return (
    <AntApp>
      <DashboardInner {...props} />
    </AntApp>
  );
}
